/**
 * Comparison content for /compare/* pages. Each entry powers a dedicated
 * page that helps decision-makers pick between a freelance SEO consultant
 * (Tamal) and the common alternatives in the Bangladesh market.
 *
 * Kept generic on purpose — we compare *categories* (agencies, in-house,
 * marketplaces) rather than naming specific competitors, which keeps the
 * pages defensible and useful for years.
 */

export type ComparisonRow = {
  feature: string;
  tamal: string;
  them: string;
  /** Optional note shown under the row. */
  note?: string;
};

export type Comparison = {
  slug: string;
  /** Short noun used inside copy, e.g. "agencies", "in-house teams". */
  themLabel: string;
  /** Heading shown in the comparison column ("vs. <heading>"). */
  themHeading: string;
  title: string;
  metaTitle: string;
  description: string;
  /** One-line summary used on the /compare hub. */
  tagline: string;
  /** Lead paragraph that opens the page (after the H1). */
  lead: string;
  /** AEO-friendly quick answer surfaced near the top + as the FAQ TL;DR. */
  quickAnswer: string;
  /** Comparison table — keep rows scannable (5–7 rows is the sweet spot). */
  rows: ComparisonRow[];
  /** Bulleted "when to pick the other option" honesty section. */
  whenToPickThem: string[];
  /** Bulleted "when to pick Tamal" section. */
  whenToPickTamal: string[];
  faq: { q: string; a: string }[];
};

export const COMPARISONS: Comparison[] = [
  {
    slug: "freelance-seo-vs-agency-bangladesh",
    themLabel: "agencies",
    themHeading: "Typical SEO agency",
    title: "Freelance SEO Expert vs. SEO Agency in Bangladesh",
    metaTitle:
      "Freelance SEO Expert vs. SEO Agency in Bangladesh — Which Wins?",
    description:
      "Honest comparison: hiring a senior freelance SEO consultant in Bangladesh vs. signing with a local SEO agency. Pricing, speed, accountability, and AEO/GEO capability — side by side.",
    tagline:
      "Senior consultant vs. junior account-managed team — what each is actually good at.",
    lead:
      "Most Bangladesh businesses comparing SEO providers end up choosing between a freelance specialist and a mid-sized agency. Both can work — but they win on very different dimensions. This page breaks down where each one is actually stronger, with no vendor spin.",
    quickAnswer:
      "A senior freelance SEO consultant like Tamal is usually the better choice for SMBs, founders, and marketing leads who want direct expert access, faster execution, and AEO/GEO capability — without paying for an agency's account-management overhead. Agencies make more sense for enterprises needing parallel work across paid media, design, and PR under one roof.",
    rows: [
      {
        feature: "Who you actually work with",
        tamal: "The senior consultant doing the work — no account managers in between.",
        them: "Account manager up front; junior executives doing the SEO work.",
      },
      {
        feature: "Monthly pricing (BDT)",
        tamal: "৳35,000 – ৳1,20,000 / mo depending on scope.",
        them: "৳60,000 – ৳3,00,000 / mo plus setup fees.",
      },
      {
        feature: "Time to first deliverable",
        tamal: "5–10 days from kickoff.",
        them: "Often longer — onboarding, audits and approval cycles come first.",
      },
      {
        feature: "AEO &amp; GEO (AI search) capability",
        tamal: "Core specialty — schema, entity SEO, llms.txt, citation tracking.",
        them: "Often absent or outsourced; still treated as a side project.",
        note: "Most BD agencies haven't shipped a real AEO program yet.",
      },
      {
        feature: "Reporting cadence",
        tamal: "Weekly Loom + monthly dashboard you actually read.",
        them: "Monthly 30-page PDF that nobody opens.",
      },
      {
        feature: "Lock-in",
        tamal: "Month-to-month after a 90-day initial sprint.",
        them: "6–12 month contracts standard.",
      },
      {
        feature: "Best fit",
        tamal: "Founders, SMBs, marketing leads who want one expert accountable end-to-end.",
        them: "Enterprises needing SEO bundled with paid, design, and PR under one vendor.",
      },
    ],
    whenToPickThem: [
      "You need SEO, paid media, design, and PR all bundled under one vendor with one invoice.",
      "Your procurement requires a registered company on the contract (not an individual).",
      "You have an internal team that prefers managing a vendor relationship over working directly with an executor.",
    ],
    whenToPickTamal: [
      "You want the person actually doing the work to be the one you talk to every week.",
      "You're serious about AI search (ChatGPT, Perplexity, AI Overviews) and need real AEO/GEO capability.",
      "You'd rather see work shipping in the first weeks than sit through long onboarding decks.",
      "Your budget is ৳35k–৳1.2L/mo and you want every taka going to work, not overhead.",
    ],
    faq: [
      {
        q: "Is a freelance SEO consultant really cheaper than a Bangladesh SEO agency?",
        a: "Yes — costs depend on the actual scope and proposal; no fixed percentage saving is established, because you're not paying for an account-management layer, sales team, or office overhead. The trade-off is that a freelancer can't run five parallel disciplines (SEO, paid, design, PR) at once; for SEO/AEO/GEO specifically, you're getting senior work at a junior price.",
      },
      {
        q: "Can one freelancer do as much as an agency team?",
        a: "For SEO, AEO, and GEO — yes, often more, because the work isn't being handed down two levels to the most junior person on the team. For multi-channel programs that also need paid media buying, brand design, and PR distribution, an agency or a small pod of freelancers is the right shape.",
      },
      {
        q: "What happens if Tamal is unavailable?",
        a: "Every retainer includes a documented Notion workspace, named backup consultants for emergencies, and a 48-hour SLA on critical issues. You're never single-threaded on a person who might be on a flight.",
      },
      {
        q: "Do agencies in Bangladesh do AEO and GEO?",
        a: "A handful are starting to, but as of 2026 most still treat AI search as either a buzzword on the website or a side experiment. If AEO/GEO matters to you, ask any agency to show you 3 client examples of citation tracking and entity SEO work before you sign.",
      },
    ],
  },
  {
    slug: "in-house-vs-freelance-seo-consultant",
    themLabel: "in-house hire",
    themHeading: "In-house SEO hire",
    title: "In-House SEO Manager vs. Freelance SEO Consultant",
    metaTitle: "In-House SEO Manager vs. Freelance SEO Consultant — 2026 Comparison",
    description:
      "Should you hire a full-time in-house SEO manager or work with a senior freelance SEO consultant? Cost, ramp-up time, expertise breadth, and AEO/GEO capability compared side by side.",
    tagline: "Full-time salary line vs. senior consultant on retainer — the real numbers.",
    lead:
      "Hiring an in-house SEO manager in Bangladesh runs ৳60k–৳1.5L/month in salary alone, plus 3–6 months of ramp-up before they're shipping at full speed. A senior freelance consultant skips the ramp and costs roughly the same — with broader expertise. Here's the honest trade-off.",
    quickAnswer:
      "An in-house SEO manager wins when you have ongoing daily SEO work across hundreds of pages and want institutional knowledge to live inside the company. A freelance consultant wins when you want senior expertise from week one, AEO/GEO capability that no junior in-house hire has yet, and a flexible monthly cost.",
    rows: [
      {
        feature: "Total monthly cost",
        tamal: "৳35k – ৳1.2L retainer; no benefits, no equipment, no laptop.",
        them: "৳60k – ৳1.5L salary + ~30% in benefits, tools, and equipment.",
      },
      {
        feature: "Ramp-up to full output",
        tamal: "1 week — already has the playbooks and tools.",
        them: "3–6 months — onboarding, tool access, brand context.",
      },
      {
        feature: "Seniority level for the budget",
        tamal: "SEO, AEO and GEO consulting; request relevant evidence.",
        them: "Junior to mid-level for that salary band in Dhaka/Rangpur.",
      },
      {
        feature: "AEO &amp; GEO capability",
        tamal: "Core specialty.",
        them: "Rare — most in-house SEOs in BD haven't shipped this yet.",
      },
      {
        feature: "Risk if they leave",
        tamal: "Documented Notion workspace; 30-day handover guaranteed.",
        them: "2–4 months of lost momentum + new hire recruiting cost.",
      },
      {
        feature: "Cross-industry pattern matching",
        tamal: "Sees patterns across 5 verticals every month.",
        them: "Only sees your industry — slower to spot what's working elsewhere.",
      },
      {
        feature: "Best fit",
        tamal: "Brands with focused SEO programs and a marketing lead who can direct work.",
        them: "Brands with massive content velocity (10+ posts/week) needing daily ops.",
      },
    ],
    whenToPickThem: [
      "You're publishing 10+ pieces of content per week and need someone in Slack all day.",
      "Your company has a strict policy that strategic functions must be in-house.",
      "You want SEO knowledge to become permanent institutional capability over 3+ years.",
    ],
    whenToPickTamal: [
      "You need senior expertise shipping from week one, not month four.",
      "Your marketing budget is too tight to cover salary + benefits + tools for a senior hire.",
      "You want AEO/GEO capability that almost no in-house candidate in Bangladesh has yet.",
      "Your content velocity is steady but not enormous (1–8 pieces/month).",
    ],
    faq: [
      {
        q: "Can I start with a freelancer and hire in-house later?",
        a: "Yes — and it's the smart path for most growing brands. Tamal explicitly supports this: the goal of the first 6–12 months is to build the system, document it in Notion, and then either keep running it or hand it over to your eventual in-house hire. The transition is part of the engagement.",
      },
      {
        q: "What about hiring a junior in-house SEO and adding a freelance consultant on top?",
        a: "This is often the best of both worlds — a ৳40k junior executing daily ops, with a senior consultant on a 10–15 hour/month strategy retainer. You get senior thinking plus cheap execution capacity, for less than a single senior salary.",
      },
      {
        q: "How does the freelance contract handle confidentiality?",
        a: "Standard NDA on signature. All client work lives in private workspaces; nothing is shared in case studies without written approval. Same legal protections you'd write into an in-house employment contract.",
      },
    ],
  },
  {
    slug: "marketplace-freelancer-vs-senior-consultant",
    themLabel: "marketplace freelancers",
    themHeading: "Marketplace freelancer (Fiverr / Upwork)",
    title: "Marketplace SEO Freelancer vs. Senior SEO Consultant",
    metaTitle:
      "Fiverr/Upwork SEO vs. Senior SEO Consultant — Why Cheap Costs More",
    description:
      "The honest difference between a ৳5,000/month Fiverr SEO gig and a senior freelance SEO consultant. Output quality, AEO/GEO capability, and what 'cheap SEO' actually costs you in 12 months.",
    tagline: "Why the cheapest SEO gig on a marketplace is almost always the most expensive.",
    lead:
      "It's tempting to start with a $15 Fiverr SEO gig or a $5/hour Upwork freelancer. The math seems obvious. But when comparing proposals, I can tell you exactly what it costs in month 12 — and it's never $15.",
    quickAnswer:
      "Marketplace SEO gigs (Fiverr, Upwork starter tier) optimize for the cheapest possible monthly invoice, which means generic checklists, link-farm backlinks, and template content. A senior consultant costs 10–20× more per month but should be evaluated through actual evidence, not a promised result multiplier because the work is custom, AEO-capable, and won't get your site penalized.",
    rows: [
      {
        feature: "Monthly cost",
        tamal: "৳35,000 – ৳1,20,000",
        them: "৳1,500 – ৳15,000",
      },
      {
        feature: "What you get",
        tamal: "Custom strategy, original content, white-hat technical work, AEO/GEO.",
        them: "Template audit PDF, recycled article copy, link-farm backlinks.",
      },
      {
        feature: "Backlink quality",
        tamal: "Manually-earned mentions from real publications.",
        them: "PBN, comment spam, web 2.0 — Google penalties waiting to happen.",
        note: "Ask for the actual link sources and method; price alone does not establish quality.",
      },
      {
        feature: "Content quality",
        tamal: "Original research, expert quotes, schema-rich.",
        them: "AI-spun rewrites or non-native English with little brand voice.",
      },
      {
        feature: "Accountability",
        tamal: "Named senior consultant; weekly Loom check-ins.",
        them: "Anonymous handle; tickets reassigned mid-project.",
      },
      {
        feature: "Real cost in 12 months",
        tamal: "৳4L – ৳14L spent → measurable revenue lift.",
        them: "৳18k – ৳1.8L spent + ৳5L+ to fix the damage later.",
      },
      {
        feature: "Best fit",
        tamal: "Brands treating SEO as a real growth channel.",
        them: "Test-and-learn micro-projects with no expectations.",
      },
    ],
    whenToPickThem: [
      "You need a one-off micro-task (single page audit, a quick robots.txt fix, a schema snippet).",
      "You're personally learning SEO and want to see what a cheap deliverable looks like.",
      "Your project is purely experimental and you can afford to throw it away.",
    ],
    whenToPickTamal: [
      "SEO matters to your revenue and you want it done right the first time.",
      "You've already been burned by a cheap freelancer and need someone to clean up the mess.",
      "You want AEO/GEO capability — marketplace gigs almost never offer this.",
      "You'd rather pay once for senior work than three times for re-do work.",
    ],
    faq: [
      {
        q: "Are all Fiverr and Upwork SEO freelancers bad?",
        a: "No — there are excellent senior freelancers on both platforms. But they charge $80–$200/hour, not $5/hour, and they're rarely the result you get when you sort by 'lowest price'. If you're filtering for cheap, you're filtering for inexperienced.",
      },
      {
        q: "Can a $50/month SEO gig actually rank my site?",
        a: "For an ultra-low-competition local query in a tiny niche, sometimes — for a few months. For anything competitive, no. SEO at that price point is mathematically impossible to do well; the freelancer is either using automation, recycled deliverables, or shortcuts that age badly.",
      },
      {
        q: "What's the most common damage caused by cheap SEO gigs?",
        a: "Three things, ranked by frequency: (1) toxic backlinks from PBNs that trigger manual penalties; (2) duplicate or AI-spun content that gets pages deindexed; (3) broken schema and incorrect canonicals that quietly kill rankings for months before anyone notices.",
      },
    ],
  },
];
